import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

export default function ModernCreateBoardModal({ isOpen, onClose, api, onBoardCreated, workspaces = [], defaultWorkspaceId = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: 'basic',
    privacy: 'private',
    workspace_id: defaultWorkspaceId
  });
  
  const [allUsers, setAllUsers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Dados básicos, 2: Membros

  useEffect(() => {
    if (isOpen) {
      fetchAllUsers();
      setFormData(prev => ({ ...prev, workspace_id: defaultWorkspaceId }));
    }
  }, [isOpen, defaultWorkspaceId]);

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleMemberSelection = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleCreateBoard = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome do quadro é obrigatório');
      return;
    }

    setLoading(true);
    try {
      // Enviar apenas os campos suportados pela API
      const boardResponse = await api.post('/projects/boards', {
        name: formData.name,
        description: formData.description,
        template: formData.template || 'basic',
        crm_integration: false,
        linked_crm_entity: null,
        workspace_id: formData.workspace_id || null
      });

      const newBoard = boardResponse.data;

      // Adicionar membros se selecionados - usar endpoint correto
      if (selectedMembers.length > 0) {
        try {
          await api.post(`/projects/boards/${newBoard.id}/invite`, {
            user_ids: selectedMembers
          });
        } catch (memberError) {
          console.warn('Warning adding members:', memberError);
          // Não falhar a criação do quadro por erro nos membros
        }
      }

      toast.success('Quadro criado com sucesso!');
      onBoardCreated?.(newBoard);
      handleClose();
    } catch (error) {
      console.error('Error creating board:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao criar quadro';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      template: 'basic',
      privacy: 'private',
      workspace_id: ''
    });
    setSelectedMembers([]);
    setSearchQuery('');
    setStep(1);
    onClose();
  };

  const filteredUsers = allUsers.filter(user => 
    searchQuery === '' || 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length > 1 
      ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase()
      : name.charAt(0).toUpperCase();
  };

  const getUserColor = (userId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 
      'bg-yellow-500', 'bg-indigo-500', 'bg-red-500', 'bg-teal-500'
    ];
    const hash = userId?.split('').reduce((a, b) => a + b.charCodeAt(0), 0) || 0;
    return colors[hash % colors.length];
  };

  const boardTemplates = [
    { 
      id: 'basic', 
      name: 'Básico', 
      description: 'Quadro simples para gerenciamento de tarefas',
      icon: '📋',
      color: 'from-blue-400 to-blue-600',
      columns: ['Tarefa', 'Status', 'Responsável', 'Data'],
      groups: ['Em Andamento', 'Concluído'],
      preview: [
        { name: 'Tarefa 1', status: '🟡 Em andamento', badge: 'bg-yellow-100 text-yellow-800' },
        { name: 'Tarefa 2', status: '🟢 Concluído', badge: 'bg-green-100 text-green-800' },
        { name: 'Tarefa 3', status: '⚪ A fazer', badge: 'bg-gray-100 text-gray-600' },
      ]
    },
    { 
      id: 'project', 
      name: 'Projeto', 
      description: 'Template completo para gerenciamento de projetos',
      icon: '🚀',
      color: 'from-purple-400 to-purple-600',
      columns: ['Item', 'Prioridade', 'Responsável', 'Prazo', 'Status'],
      groups: ['Fase 1 – Planejamento', 'Fase 2 – Execução', 'Revisão'],
      preview: [
        { name: 'Planejamento', status: '🔴 Alta', badge: 'bg-red-100 text-red-800' },
        { name: 'Desenvolvimento', status: '🟡 Média', badge: 'bg-yellow-100 text-yellow-800' },
        { name: 'Testes', status: '🟢 Baixa', badge: 'bg-green-100 text-green-800' },
      ]
    },
    { 
      id: 'kanban', 
      name: 'Kanban', 
      description: 'Quadro estilo Kanban para fluxo de trabalho',
      icon: '📊',
      color: 'from-indigo-400 to-indigo-600',
      columns: ['Cartão', 'Coluna', 'Responsável'],
      groups: ['A Fazer', 'Em Progresso', 'Em Revisão', 'Feito'],
      preview: [
        { name: 'Backlog', status: '⬜ A Fazer', badge: 'bg-gray-100 text-gray-600' },
        { name: 'Em Dev', status: '🔵 Em Progresso', badge: 'bg-blue-100 text-blue-800' },
        { name: 'Revisar', status: '🟣 Em Revisão', badge: 'bg-purple-100 text-purple-800' },
      ]
    },
    { 
      id: 'sprint', 
      name: 'Sprint', 
      description: 'Template para desenvolvimento ágil com pontos de história',
      icon: '⚡',
      color: 'from-orange-400 to-orange-600',
      columns: ['História', 'Pontos', 'Sprint', 'Status', 'Responsável'],
      groups: ['Sprint 1', 'Sprint 2', 'Backlog'],
      preview: [
        { name: 'US-001 Login', status: '5 pts', badge: 'bg-orange-100 text-orange-800' },
        { name: 'US-002 Dashboard', status: '8 pts', badge: 'bg-orange-100 text-orange-800' },
        { name: 'US-003 Perfil', status: '3 pts', badge: 'bg-orange-100 text-orange-800' },
      ]
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center text-white font-bold">
              +
            </div>
            <span>Criar Novo Quadro</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-6">
            {/* Dados básicos do quadro */}
            <div className="space-y-4">
              {/* Workspace selector */}
              {workspaces.length > 0 && (
                <div>
                  <Label htmlFor="workspace">Workspace</Label>
                  <select
                    id="workspace"
                    value={formData.workspace_id}
                    onChange={e => handleInputChange('workspace_id', e.target.value)}
                    className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                  >
                    <option value="">— Sem workspace —</option>
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <Label htmlFor="name">Nome do Quadro *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Projeto Marketing Q4"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Descreva o objetivo deste quadro..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Privacidade</Label>
                <Select value={formData.privacy} onValueChange={(value) => handleInputChange('privacy', value)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">🔒 Privado - Apenas membros convidados</SelectItem>
                    <SelectItem value="team">👥 Equipe - Visível para toda equipe</SelectItem>
                    <SelectItem value="public">🌍 Público - Visível para todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Templates */}
            <div>
              <Label>Escolha um Template</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {boardTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`border-2 rounded-xl cursor-pointer transition-all overflow-hidden ${
                      formData.template === template.id ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => handleInputChange('template', template.id)}
                  >
                    {/* Visual Header */}
                    <div className={`bg-gradient-to-r ${template.color} p-3`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-lg">{template.icon}</span>
                        <span className="text-white text-xs font-semibold">{template.name}</span>
                        {formData.template === template.id && (
                          <span className="text-white text-xs bg-white/30 rounded-full px-1.5 py-0.5">✓</span>
                        )}
                      </div>
                      {/* Mini column headers */}
                      <div className="flex space-x-1">
                        {template.columns.slice(0, 3).map((col, i) => (
                          <span key={i} className="text-white/80 text-[9px] bg-white/20 rounded px-1.5 py-0.5 truncate">{col}</span>
                        ))}
                        {template.columns.length > 3 && (
                          <span className="text-white/60 text-[9px]">+{template.columns.length - 3}</span>
                        )}
                      </div>
                    </div>
                    {/* Mini row preview */}
                    <div className="bg-white p-2 space-y-1">
                      {template.preview.map((row, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[10px] text-gray-700 truncate flex-1">{row.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ml-1 ${row.badge}`}>{row.status}</span>
                        </div>
                      ))}
                      <div className="text-[9px] text-gray-400 pt-0.5">{template.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Convite de membros */}
            <div>
              <h3 className="text-lg font-medium mb-4">Convidar Membros</h3>
              <p className="text-sm text-gray-600 mb-4">
                Selecione os usuários que terão acesso a este quadro. Você pode adicionar mais membros depois.
              </p>
            </div>

            {/* Barra de busca */}
            <div>
              <Label htmlFor="search-members">Buscar usuários</Label>
              <Input
                id="search-members"
                placeholder="Digite o nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Membros selecionados */}
            {selectedMembers.length > 0 && (
              <div>
                <Label>Membros Selecionados ({selectedMembers.length})</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedMembers.map(userId => {
                    const user = allUsers.find(u => u.id === userId);
                    return (
                      <Badge 
                        key={userId} 
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 flex items-center space-x-1"
                      >
                        <div 
                          className={`w-4 h-4 rounded-full flex items-center justify-center text-white text-xs ${getUserColor(userId)}`}
                        >
                          {getUserInitials(user?.name)}
                        </div>
                        <span>{user?.name || 'Usuário'}</span>
                        <button
                          onClick={() => toggleMemberSelection(userId)}
                          className="ml-1 hover:text-red-600"
                        >
                          ×
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lista de usuários */}
            <div>
              <Label>Usuários Disponíveis</Label>
              <div className="mt-2 max-h-64 overflow-y-auto border rounded-lg">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center space-x-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      selectedMembers.includes(user.id) ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => toggleMemberSelection(user.id)}
                  >
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${getUserColor(user.id)}`}
                    >
                      {getUserInitials(user.name)}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                    <div className="flex items-center">
                      {selectedMembers.includes(user.id) && (
                        <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {filteredUsers.length === 0 && (
                  <div className="p-4 text-center text-gray-500">
                    <p>Nenhum usuário encontrado</p>
                    {searchQuery && <p className="text-sm mt-1">Tente buscar com outros termos</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {step === 2 && (
              <Button variant="outline" onClick={() => setStep(1)}>
                ← Voltar
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            
            {step === 1 ? (
              <Button 
                onClick={() => setStep(2)}
                disabled={!formData.name.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Próximo: Membros →
              </Button>
            ) : (
              <Button 
                onClick={handleCreateBoard}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? '...' : `Criar Quadro ${selectedMembers.length > 0 ? `(+${selectedMembers.length} membros)` : ''}`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}